import { EMPTY_ARRAY } from '@unifyapps/carbon/consts/empty';
import type { InterfacePathNode } from '@unifyapps/defs/types/interface';
import { BASE_PATH, DYNAMIC_PARAM_PREFIX, PATH_NODE_PREFIX, PATH_ROOT_ID } from '../../const';
import {
  getNormalizedPathFromSlugOrPathNames,
  getPathSegmentsFromNormalizedPath,
  type InterfacePageParam,
} from './interfacePathNormalize';

type ResolveInput = {
  pageParam: InterfacePageParam;
  pathNodes?: Record<string, InterfacePathNode>;
  pageIdToSlugMap?: Record<string, string | undefined>;
};

type MatchPathResult = {
  node?: InterfacePathNode;
  params: Record<string, string>;
};

export type ResolveResult = {
  /**
   * complete pathname the user visited the, i.e /admins/users/123/details
   */
  requestedPath: string;
  /**
   * last part of the pathname the user visited, i.e details
   */
  resolvedSlug: string;
  /**
   * page id of the page the user visited, i.e p_user_details, extracted by interface record
   */
  pageId?: string;
  /**
   * if the pageId had dynamic params, then this will be the params, i.e { userId: '123' }
   */
  params?: Record<string, string>;
  /**
   * if the visited pathname is leading to a folder that has redirect configured, then this will be the redirect target
   */
  redirect?: {
    to: string;
    params?: Record<string, string>;
  };
};

export type PathResult = {
  fullPath: string;
  pageId: string;
  slug: string;
};

function matchPath(
  pathNodes: Record<string, InterfacePathNode> | undefined,
  segments: string[],
  nodeId = PATH_ROOT_ID,
  params: Record<string, string> = {},
  pageSlug?: string,
  pageIdToSlugMap?: Record<string, string | undefined>,
  visited: Set<string> = new Set<string>(),
): MatchPathResult & { pageId?: string } {
  if (!pathNodes) {
    return { node: undefined, params: {} };
  }

  const visitKey = `${nodeId}:${segments.length}`;
  if (visited.has(visitKey)) {
    return { node: undefined, params };
  }

  const nextVisited = new Set<string>(visited);
  nextVisited.add(visitKey);

  const node = pathNodes[nodeId];
  if (!node) {
    return { node: undefined, params };
  }

  // If no more segments → check slug (if provided)
  if (segments.length === 0) {
    if (pageSlug && pageIdToSlugMap) {
      const matchedPageId = node.children
        ?.filter((childId) => !childId.startsWith(PATH_NODE_PREFIX))
        .find((pageId) => pageIdToSlugMap[pageId] === pageSlug);

      if (matchedPageId) {
        return { node, params, pageId: matchedPageId };
      }

      return { node: undefined, params };
    }

    return { node, params };
  }

  const [segment, ...rest] = segments;

  const childIds = node.children?.filter((childId) => childId.startsWith(PATH_NODE_PREFIX)) ?? [];
  const childNodes = childIds.map((childId) => pathNodes[childId]).filter(Boolean);

  for (const child of childNodes) {
    if (child.segment === segment) {
      const result = matchPath(
        pathNodes,
        rest,
        child.id,
        { ...params },
        pageSlug,
        pageIdToSlugMap,
        nextVisited,
      );

      if (result.node) return result;
    }
  }

  for (const child of childNodes) {
    if (child.segment.startsWith(DYNAMIC_PARAM_PREFIX)) {
      const newParams = {
        ...params,
        [child.segment.slice(1)]: segment,
      };

      const resultWithCycleGuard = matchPath(
        pathNodes,
        rest,
        child.id,
        newParams,
        pageSlug,
        pageIdToSlugMap,
        nextVisited,
      );

      if (resultWithCycleGuard.node) return resultWithCycleGuard;
    }
  }

  return { node: undefined, params };
}

/**
 * Resolves a URL path param (e.g. from Next.js `[...interfacePageSlug]`) to a page ID, slug,
 * dynamic route params, or a redirect instruction.
 *
 * The input `pageParam` is split into:
 *   - `pathSegments` — all segments except the last (the folder path)
 *   - `pageSlug`     — the last segment (the page identifier)
 *
 * Resolution has three modes:
 *
 * 1. **No path tree** (`pathNodes` is absent — legacy interfaces):
 *    Falls back to a direct slug → pageId reverse-lookup via `pageIdToSlugMap`.
 *    No dynamic params, no redirect support.
 *
 * 2. **Folder path match** (primary):
 *    Walks the path tree using `pathSegments` to find the parent folder, collecting
 *    dynamic param values (e.g. `:userId → '42'`) along the way, then confirms
 *    `pageSlug` is a child of that folder. Returns `pageId` + `params`.
 *
 * 3. **Full path redirect match** (fallback):
 *    Re-runs the tree walk with all segments (including the page slug treated as a folder
 *    segment) to check if any node at that path has a `redirect` configured. Returns the
 *    redirect target so the caller can issue a 301/302.
 *
 * Returns an empty result (no pageId, no redirect) if nothing matches — caller should 404.
 */
export function resolvePageFromPathForPathAndSlug({
  pageParam,
  pathNodes,
  pageIdToSlugMap,
}: ResolveInput): ResolveResult {
  const pathFromParam = getNormalizedPathFromSlugOrPathNames(pageParam);
  const segments = getPathSegmentsFromNormalizedPath(pathFromParam);
  const requestedPath = segments.join(BASE_PATH);
  const pageSlug = segments[segments.length - 1] || '';
  const pathSegments = segments.slice(0, -1);

  // Backward compatibility for interfaces with no pathNodes, that means there are not nested pathnames present and all have just one slug
  if (!pathNodes) {
    // If more than 1 segment , nested path , give 404
    if (segments.length > 1) {
      return {
        requestedPath,
        resolvedSlug: pageSlug,
      };
    }

    const pageId = pageIdToSlugMap
      ? Object.entries(pageIdToSlugMap).find(([, slug]) => slug === pageSlug)?.[0]
      : undefined;

    return {
      requestedPath,
      resolvedSlug: pageSlug,
      pageId,
      params: {},
    };
  }

  const match = matchPath(pathNodes, pathSegments, PATH_ROOT_ID, {}, pageSlug, pageIdToSlugMap);

  if (match.pageId) {
    return {
      requestedPath,
      resolvedSlug: pageSlug,
      pageId: match.pageId,
      params: match.params,
    };
  }

  const fullMatch = matchPath(pathNodes, segments);

  if (fullMatch.node?.redirect) {
    const { redirect } = fullMatch.node;
    // resolve pageId to the live path so renamed slugs/folders still redirect; a missing
    // or unresolvable pageId (deleted target) drops the redirect.
    const to = redirect.pageId
      ? getFullRawNonNormalizedPathByPageId({ pathNodes, pageIdToSlugMap, pageId: redirect.pageId })
      : undefined;

    if (to) {
      return {
        requestedPath,
        resolvedSlug: pageSlug,
        redirect: { to, params: redirect.params },
        params: fullMatch.params,
      };
    }
  }

  return {
    requestedPath,
    resolvedSlug: pageSlug,
  };
}

/**
 * Resolves every page's full raw path from the interface path tree.
 *
 * @param pathNodes - the interface path tree to traverse
 * @param pageIdToSlugMap - maps page ids to their slugs
 * @returns all resolved page paths with their page ids and slugs
 */
export function getAllInterfacePageFullRawPaths({
  pathNodes,
  pageIdToSlugMap,
}: {
  pathNodes?: Record<string, InterfacePathNode>;
  pageIdToSlugMap?: Record<string, string | undefined>;
}): PathResult[] {
  if (!pathNodes || !pageIdToSlugMap) return EMPTY_ARRAY as PathResult[];

  const results: PathResult[] = [];

  // resolve paths per-page using the single-item helper
  // `getFullRawPathByPageId`. This centralizes logic and avoids duplicating DFS.
  for (const pageId of Object.keys(pageIdToSlugMap)) {
    const slug = pageIdToSlugMap[pageId];
    if (!slug) continue;

    const fullPath = getFullRawNonNormalizedPathByPageId({ pageId, pathNodes, pageIdToSlugMap });
    if (!fullPath) continue;

    results.push({ fullPath, pageId, slug });
  }
  return results;
}

/**
 * Resolves the full raw path for a single page id from the interface path tree.
 *
 * @param pageId - the page id to resolve
 * @param pathNodes - the interface path tree to traverse
 * @param pageIdToSlugMap - maps page ids to their slugs
 * @returns the full page path, or undefined if the page cannot be resolved
 */
export function getFullRawNonNormalizedPathByPageId({
  pageId,
  pathNodes,
  pageIdToSlugMap,
}: {
  pageId: string;
  pathNodes?: Record<string, InterfacePathNode>;
  pageIdToSlugMap?: Record<string, string | undefined>;
}): string | undefined {
  if (!pathNodes) {
    return undefined;
  }

  const pageSlug = pageIdToSlugMap?.[pageId];
  if (!pageSlug) {
    return undefined;
  }

  const rootNode = pathNodes[PATH_ROOT_ID];
  if (!rootNode) {
    return undefined;
  }

  const dfs = (
    node: InterfacePathNode,
    currentPath: string,
    visited: Set<string> = new Set<string>(),
  ): string | undefined => {
    if (visited.has(node.id)) {
      return undefined;
    }

    const nextVisited = new Set<string>(visited);
    nextVisited.add(node.id);

    let basePath = BASE_PATH;

    if (node.segment === '') {
      basePath = BASE_PATH;
    } else if (currentPath === BASE_PATH) {
      basePath = `/${node.segment}`;
    } else {
      basePath = `${currentPath}/${node.segment}`;
    }

    if ((node.children ?? []).includes(pageId)) {
      return basePath;
    }

    const childFolderIds = (node.children ?? []).filter((childId) =>
      childId.startsWith(PATH_NODE_PREFIX),
    );

    for (const childId of childFolderIds) {
      const childNode = pathNodes[childId];
      if (!childNode) {
        continue;
      }

      const fullPath = dfs(childNode, basePath, nextVisited);
      if (fullPath) {
        return fullPath;
      }
    }

    return undefined;
  };

  const basePath = dfs(rootNode, BASE_PATH);

  if (!basePath) {
    return undefined;
  }

  return basePath === BASE_PATH ? `/${pageSlug}` : `${basePath}/${pageSlug}`;
}

/**
 * Collects builder values for dynamic params on the path to a page id.
 *
 * @param pageId - the page id whose ancestor params should be resolved
 * @param pathNodes - the interface path tree to traverse
 * @param pageIdToSlugMap - maps page ids to their slugs
 * @returns a map of dynamic param names to their builder values
 */
export function getBuilderValueByParamNameForPageId({
  pageId,
  pathNodes,
  pageIdToSlugMap,
}: {
  pageId: string;
  pathNodes?: Record<string, InterfacePathNode>;
  pageIdToSlugMap?: Record<string, string | undefined>;
}): Record<string, string | undefined> {
  const fullPath = getFullRawNonNormalizedPathByPageId({ pageId, pathNodes, pageIdToSlugMap });

  if (!fullPath || !pathNodes?.[PATH_ROOT_ID]) {
    return {};
  }

  const collectedValues: Record<string, string | undefined> = {};
  const pathSegments = getPathSegmentsFromNormalizedPath(fullPath);
  const folderSegments = pathSegments.slice(0, -1);

  const traverse = (
    nodeId: string,
    segmentIndex: number,
    visited: Set<string> = new Set<string>(),
  ): boolean => {
    const visitKey = `${nodeId}:${segmentIndex}`;
    if (visited.has(visitKey)) {
      return false;
    }

    const nextVisited = new Set<string>(visited);
    nextVisited.add(visitKey);

    const node = pathNodes[nodeId];

    if (!node) {
      return false;
    }

    if (node.segment.startsWith(DYNAMIC_PARAM_PREFIX)) {
      collectedValues[node.segment.slice(1)] = node.builderValue;
    }

    if (segmentIndex >= folderSegments.length) {
      return (node.children ?? []).includes(pageId);
    }

    const nextSegment = folderSegments[segmentIndex];
    const childFolderIds = (node.children ?? []).filter((childId) =>
      childId.startsWith(PATH_NODE_PREFIX),
    );

    for (const childId of childFolderIds) {
      const childNode = pathNodes[childId];

      if (childNode?.segment === nextSegment && traverse(childId, segmentIndex + 1, nextVisited)) {
        return true;
      }
    }

    return false;
  };

  traverse(PATH_ROOT_ID, 0);

  return collectedValues;
}
